/** Wildcard sentinel used by the report-defaults matrix ('any modality/species/body area'). */
export const REPORT_DEFAULT_WILDCARD = '*';
/**
 * Stable identity for a matrix cell: the (modality, species, bodyArea) triple.
 * Case-insensitive (all dimensions lowercased) so client-side dedupe matches
 * the backend's duplicate validation, which compares lowercased dimensions.
 */
export function reportDefaultKey(row) {
    return [
        normalizeDimension(row.modality).toLowerCase(),
        normalizeDimension(row.species).toLowerCase(),
        normalizeDimension(row.bodyArea).toLowerCase(),
    ].join('|');
}
/** Trim a dimension value; empty means "any" and collapses to the wildcard. */
export function normalizeDimension(value) {
    const trimmed = (value ?? '').trim();
    return trimmed === '' ? REPORT_DEFAULT_WILDCARD : trimmed;
}
/**
 * Serialize the editor's draft rows into the PUT payload: dimensions trimmed
 * (empty -> '*'), rows without a template dropped, duplicate
 * (modality, species, bodyArea) cells collapsed keeping the FIRST occurrence
 * (matches the editor's top-down reading order).
 */
export function serializeReportDefaults(rows) {
    const seen = new Set();
    const result = [];
    for (const row of rows) {
        if (!row.templateId)
            continue;
        const normalized = {
            modality: normalizeDimension(row.modality),
            species: normalizeDimension(row.species),
            bodyArea: normalizeDimension(row.bodyArea),
            templateId: row.templateId,
        };
        const key = reportDefaultKey(normalized);
        if (seen.has(key))
            continue;
        seen.add(key);
        result.push(normalized);
    }
    return result;
}
/**
 * True when the draft rows would persist a different defaults set than the
 * server currently holds (order-insensitive: the matrix is a keyed set).
 */
export function reportDefaultsDiffer(draft, saved) {
    const a = serializeReportDefaults(draft);
    const b = serializeReportDefaults(saved.map((r) => ({ ...r })));
    if (a.length !== b.length)
        return true;
    const byKey = new Map(b.map((r) => [reportDefaultKey(r), r.templateId]));
    return a.some((r) => byKey.get(reportDefaultKey(r)) !== r.templateId);
}
